/**
 * main.tsx — Application Entry Point
 *
 * Mounts the React application into the DOM.
 *
 * React 18 concurrent mode is enabled via createRoot. This enables:
 *   - Concurrent rendering features (Suspense, transitions)
 *   - Automatic batching of state updates
 *   - Improved performance for complex UI updates
 *
 * StrictMode is enabled in all environments to surface:
 *   - Double-invocation of lifecycle functions (detects side effects in render)
 *   - Deprecated API warnings
 *   - Ref-forwarding issues
 *
 * NOTE: StrictMode double-invokes effects in development. This is intentional
 * and helps detect missing cleanup functions in useEffect.
 *
 * Demo mode:
 *   When VITE_DEMO_MODE=true, MSW (Mock Service Worker) is started before the
 *   React app mounts. This intercepts all API calls and returns realistic seed
 *   data without requiring any backend or Firebase connection. The app renders
 *   normally — only the data source changes.
 *
 *   The MSW worker is started asynchronously. We await its initialisation
 *   before calling createRoot so that the very first render of the app already
 *   has the mock interceptors in place and no real network requests escape.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';

// ---------------------------------------------------------------------------
// Demo mode detection
// ---------------------------------------------------------------------------

const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function bootstrap(): Promise<void> {
  // Start MSW BEFORE mounting React so no requests escape to the network.
  if (isDemoMode) {
    const { worker } = await import('./mocks/browser');

    await worker.start({
      /**
       * 'bypass': requests that don't match any handler pass through to the
       * real network. This prevents MSW from erroring on asset requests,
       * Google Fonts, dicebear avatar URLs, etc.
       */
      onUnhandledRequest: 'bypass',

      /**
       * Quiet the MSW startup log in the browser console during the
       * presentation. Unhandled-request warnings are still shown.
       */
      quiet: false,

      serviceWorker: {
        /**
         * Path to the MSW service worker script.
         * The file lives at frontend/public/mockServiceWorker.js and is served
         * from the root so that the worker scope covers all paths.
         *
         * Generate it once with:
         *   npx msw init public/ --save
         */
        url: '/mockServiceWorker.js',
      },
    });

    console.info(
      '[PhiloFund Demo] MSW started — all API requests are intercepted by mock handlers.',
    );
  }

  // ---------------------------------------------------------------------------
  // Mount React
  // ---------------------------------------------------------------------------

  const rootElement = document.getElementById('root');

  if (rootElement === null) {
    throw new Error(
      '[PhiloFund] Root element #root not found in the DOM. ' +
      'Check index.html to ensure <div id="root"></div> is present.',
    );
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App isDemoMode={isDemoMode} />
    </StrictMode>,
  );
}

// Kick off — unhandled rejection will surface in the browser console.
void bootstrap();
